import { Button, ButtonGroup, Icon, Intent, Spinner, Tooltip } from "@blueprintjs/core"
import { useState } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { MediumText } from "../../../../styled/text.styled"
import { StyledEditableText, StyledSwitch } from "../Dataviz.styled"
import { Dashboard, Frequency, Source } from "../types/dashboard"
import { DashboardUpdateCreateForm } from "../types/dashboardForm"
import { BulkAddDatasetModal } from "./BulkAddDatasetModal"
import { DashboardSelect } from "./DashboardSelect"
import {
  BottomCenter,
  BottomLeft,
  BottomRight,
  BottomRow,
  DatavizHeaderContainer,
  TopRow,
  TopRowLeft,
  TopRowRight,
} from "./DatavizHeader.styled"
import { SingleAddModal } from "./SingleAddModal"

interface DatavizHeaderProps {
  onDashboardSelect: (dashboard: Dashboard | null) => void
  onSave: () => void
  onRefresh: () => void
  showChart: boolean
  onViewChange: (showChart: boolean) => void
  hasData: boolean
  handleDashboardSelect: (dashboard: Dashboard | null) => void
  isLoading: boolean
  isSaving: boolean
  canRunPrediction: boolean
  onRunPrediction: () => void
  selectedDashboard: Dashboard | null | undefined
}

export const DatavizHeader = ({
  onDashboardSelect,
  onSave,
  onRefresh,
  showChart,
  onViewChange,
  hasData,
  handleDashboardSelect,
  isLoading,
  isSaving,
  canRunPrediction,
  onRunPrediction,
  selectedDashboard,
}: DatavizHeaderProps) => {
  const [isBulkAddOpen, setIsBulkAddOpen] = useState(false)
  const [isSingleAddOpen, setIsSingleAddOpen] = useState(false)
  const {
    watch,
    control,
    formState: { isDirty, errors, isValid },
    setValue,
  } = useFormContext<DashboardUpdateCreateForm>()

  const items = watch("items")
  const watchedSource = watch("source")

  const handleClearAll = () => {
    setValue("items", [], { shouldDirty: true, shouldValidate: true })
  }

  return (
    <DatavizHeaderContainer>
      <TopRow>
        <TopRowLeft>
          {isDirty && errors.name && (
            <Tooltip content={errors.name.message}>
              <Icon icon="error" intent="danger" />
            </Tooltip>
          )}
          <DashboardSelect onSelect={onDashboardSelect} currentDashboard={selectedDashboard ?? null} />
          <Button icon="add" intent={Intent.PRIMARY} onClick={() => handleDashboardSelect(null)}>
            New
          </Button>
          <Button
            icon="saved"
            intent={isValid ? Intent.WARNING : Intent.PRIMARY}
            onClick={onSave}
            loading={isSaving}
            disabled={!isValid || !isDirty}
          >
            Save
          </Button>
          <Button minimal icon="refresh" onClick={onRefresh} disabled={isLoading} title="Refresh" />
          {isLoading && <Spinner size={16} />}
        </TopRowLeft>
        <TopRowRight>
          {watchedSource === Source.PREDICTED && (
            <>
              <Button
                minimal
                icon="predictive-analysis"
                intent={Intent.PRIMARY}
                onClick={onRunPrediction}
              >
                New prediction
              </Button>
              <Controller
                control={control}
                name="freq"
                render={({ field: { value, onChange } }) => (
                  <ButtonGroup>
                    <Button
                      active={value === Frequency.MONTHLY}
                      onClick={() => {
                        onChange(Frequency.MONTHLY)
                      }}
                    >
                      M
                    </Button>
                    <Button
                      active={value === Frequency.QUARTERLY}
                      onClick={() => {
                        onChange(Frequency.QUARTERLY)
                      }}
                    >
                      Q
                    </Button>
                    <Button
                      active={value === Frequency.HALF_YEAR}
                      onClick={() => {
                        onChange(Frequency.HALF_YEAR)
                      }}
                    >
                      H
                    </Button>
                    <Button
                      active={value === Frequency.YEARLY}
                      onClick={() => {
                        onChange(Frequency.YEARLY)
                      }}
                    >
                      Y
                    </Button>
                  </ButtonGroup>
                )}
              />
            </>
          )}
          <Controller
            control={control}
            name="source"
            render={({ field: { value, onChange } }) => (
              <ButtonGroup>
                <Button
                  active={value === "observed"}
                  onClick={() => {
                    onChange("observed")
                  }}
                  icon="eye-open"
                >
                  Observed
                </Button>
                <Button
                  active={value === "predicted"}
                  onClick={() => {
                    onChange("predicted")
                  }}
                  icon="timeline-bar-chart"
                >
                  Predicted
                </Button>
              </ButtonGroup>
            )}
          />
          <ButtonGroup>
            <Button
              large
              icon="th"
              active={!showChart}
              onClick={() => onViewChange(false)}
              disabled={!hasData}
            />
            <Button
              large
              icon="chart"
              active={showChart}
              onClick={() => onViewChange(true)}
              disabled={!hasData}
            />
          </ButtonGroup>
        </TopRowRight>
      </TopRow>

      <BottomRow>
        <BottomLeft>
          <Controller
            control={control}
            name="zScore"
            render={({ field: { onChange, value, onBlur } }) => (
              <StyledSwitch
                checked={value}
                onChange={e => {
                  onChange(e.target.checked)
                  onBlur()
                }}
                label="Z-Score"
                alignIndicator="right"
              />
            )}
          />
          <Controller
            control={control}
            name="backtest"
            render={({ field: { onChange, value, onBlur } }) => (
              <StyledSwitch
                checked={value}
                onChange={e => {
                  onChange(e.target.checked)
                  onBlur()
                }}
                label="Backtest"
                alignIndicator="right"
              />
            )}
          />
        </BottomLeft>
        <BottomCenter>
          <Icon icon="dashboard" size={16} />
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, value } }) => (
              <StyledEditableText
                value={value}
                onChange={onChange}
                placeholder="Enter dashboard name..."
                intent={errors.name ? Intent.DANGER : Intent.NONE}
              />
            )}
          />
        </BottomCenter>
        <BottomRight>
          <Button
            icon="add"
            minimal
            intent={Intent.PRIMARY}
            onClick={() => setIsSingleAddOpen(true)}
          />
          <Button
            icon="multi-select"
            minimal
            intent="primary"
            onClick={() => setIsBulkAddOpen(true)}
            title="Bulk Add Datasets"
          />
          <Button
            icon="trash"
            minimal
            intent={Intent.DANGER}
            onClick={handleClearAll}
            disabled={items?.length === 0}
          >
            Clear All
          </Button>
          {errors.items && (
            <Tooltip content={errors.items.message}>
              <Icon icon="error" intent="danger" />
            </Tooltip>
          )}
          <Icon icon="selection" size={16} />
          <MediumText>{items?.length} selected</MediumText>
        </BottomRight>
      </BottomRow>

      <SingleAddModal isOpen={isSingleAddOpen} setIsOpen={setIsSingleAddOpen} />
      <BulkAddDatasetModal isOpen={isBulkAddOpen} setIsOpen={setIsBulkAddOpen} />
    </DatavizHeaderContainer>
  )
}
